import { useEffect, useRef, useState } from 'react'
import video2 from '../assets/videos/landing-2.mp4'
import video3 from '../assets/videos/landing-3.mp4'
import video4 from '../assets/videos/landing-4.mp4'
import video5 from '../assets/videos/landing-5.mp4'
import video6 from '../assets/videos/landing-6.mov'

interface VideoBackgroundProps {
  className?: string
}

export const VideoBackground = ({ className = '' }: VideoBackgroundProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)

  // Define video sources with proper imports
  const videos = [video2, video3, video4, video5, video6]

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Set up video for optimal streaming
    video.preload = 'metadata' // Only load metadata initially
    video.playsInline = true // Important for mobile

    // Handle video end - move to next video
    const handleVideoEnd = () => {
      setCurrentVideoIndex((prev) => (prev + 1) % videos.length)
    }

    // Handle errors by moving to next video
    const handleError = () => {
      console.warn(
        `Error loading video ${videos[currentVideoIndex]}, moving to next`,
      )
      setCurrentVideoIndex((prev) => (prev + 1) % videos.length)
    }

    // Start playing when video is ready
    const handleCanPlay = () => {
      video.play().catch((err) => {
        console.warn('Autoplay prevented:', err)
        // Some browsers prevent autoplay - we'll handle this gracefully
      })
    }

    video.addEventListener('ended', handleVideoEnd)
    video.addEventListener('error', handleError)
    video.addEventListener('canplay', handleCanPlay)

    return () => {
      video.removeEventListener('ended', handleVideoEnd)
      video.removeEventListener('error', handleError)
      video.removeEventListener('canplay', handleCanPlay)
    }
  }, [currentVideoIndex])

  // Videos that need zoom to remove letterbox (indices for videos 3, 5, and 6)
  const cinematicVideos = [1, 3, 4] // landing-3.mp4, landing-5.mp4, landing-6.mov
  const needsZoom = cinematicVideos.includes(currentVideoIndex)

  return (
    <div className={`absolute inset-0 overflow-hidden bg-black ${className}`}>
      <video
        ref={videoRef}
        className='absolute inset-0 w-full h-full object-cover'
        style={{
          objectFit: 'cover',
          minWidth: '100%',
          minHeight: '100%',
          transform: needsZoom ? 'scale(1.4)' : 'scale(1)',
        }}
        src={videos[currentVideoIndex]}
        muted
        loop={false} // We'll handle transitions manually
        autoPlay
        playsInline
      />
      {/* Very subtle overlay to blend with network graph */}
      <div className='absolute inset-0 bg-black/5' />
    </div>
  )
}
